import styled, { css, keyframes } from 'styled-components';
import type { ColorAndContrastSetting } from '../../../types';
import { DEMO_CONTROLS_CLASS } from '../../constants';
import { useDemoToggleAnimation } from '../../hooks/use-demo-toggle-animation';

type ToggleButtonsProps = {
  colorsContrast?: ColorAndContrastSetting;
};

export function ToggleButtons({ colorsContrast }: ToggleButtonsProps) {
  const {
    isBefore,
    isAfter,
    isAfterAnimating,
    afterAnimationRunId,
    handleBeforeClick,
    handleAfterClick,
    handleAfterAnimationEnd
  } = useDemoToggleAnimation();

  const isHighContrast = colorsContrast === 'high-contrast';

  return (
    <ToggleContainer
      $isAfter={isAfter}
      $colorsContrast={colorsContrast}
      className={DEMO_CONTROLS_CLASS}
    >
      <ToggleButton
        $active={isBefore}
        $isAnimating={false}
        $isHighContrast={isHighContrast}
        aria-pressed={isBefore}
        onClick={handleBeforeClick}
      >
        Before
      </ToggleButton>
      <ToggleButton
        key={afterAnimationRunId}
        $active={isAfter}
        $isAnimating={isAfterAnimating}
        $isHighContrast={isHighContrast}
        aria-pressed={isAfter}
        onClick={handleAfterClick}
        onAnimationEnd={handleAfterAnimationEnd}
      >
        After
      </ToggleButton>
    </ToggleContainer>
  );
}

function getCompensatingFilter({
  isAfter,
  colorsContrast
}: {
  isAfter: boolean;
  colorsContrast?: ColorAndContrastSetting;
}) {
  if (colorsContrast === 'inverted-colors') {
    return 'invert(100%)';
  }

  if (colorsContrast === 'high-contrast' && isAfter) {
    return 'contrast(80%)';
  }

  return 'none';
}

const ToggleContainer = styled.div<{
  $isAfter: boolean;
  $colorsContrast?: ColorAndContrastSetting;
}>`
  ${({ $isAfter, $colorsContrast }) => css`
    display: flex;
    border-radius: 50px;
    background: rgb(242 242 242 / 90%);
    padding: 2px;
    width: 220px;
    position: relative;
    filter: ${getCompensatingFilter({
      isAfter: $isAfter,
      colorsContrast: $colorsContrast
    })};

    &::before {
      content: '';
      position: absolute;
      top: 2px;
      bottom: 2px;
      width: calc(50% - 2px);
      left: 2px;
      transform: translateX(${$isAfter ? '100%' : '0'});
      background: #ffffff;
      border-radius: 50px;
      transition: transform 0.5s;
      will-change: transform;
    }

    @media (prefers-reduced-motion: reduce) {
      &::before {
        transition: none;
      }
    }
  `}
`;

const gradientAnimation = keyframes`
  0% {
    background-position: 200% 0px;
  }
  100% {
    background-position: 0px 0px;
  }
`;

const ToggleButton = styled.button<{
  $active: boolean;
  $isAnimating: boolean;
  $isHighContrast: boolean;
}>`
  ${({ $active, $isAnimating, $isHighContrast }) => css`
    flex: 1;
    padding: 6px 12px;
    border: none;
    border-radius: 50px;
    background: transparent;
    color: rgb(17 17 17 / ${$active ? 100 : 50}%);
    font-size: 13px;
    line-height: 16px;
    font-weight: 700;
    cursor: pointer;
    transition: color 0.5s;
    z-index: 1;

    ${$isAnimating &&
    css`
      background: linear-gradient(
          to right,
          ${$isHighContrast ? 'rgb(17 17 17 / 15%)' : 'transparent'} 0%,
          ${$isHighContrast ? 'rgb(17 17 17 / 100%)' : 'currentColor'} 50%,
          ${$isHighContrast ? 'rgb(17 17 17 / 15%)' : 'transparent'} 100%
        )
        0px 50px / 200% 200% transparent;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      animation: ${gradientAnimation} 2s linear;

      @media (prefers-reduced-motion: reduce) {
        -webkit-text-fill-color: unset;
        background: unset;
        animation: none;
      }
    `}

    &:focus-visible {
      outline: 3px solid #197bff;
      outline-offset: 2px;
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  `}
`;
